import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * TimePickerInput - A user-friendly time picker with scrollable dropdown.
 * Drop-in replacement for <input type="time" />
 *
 * Props:
 *   value       - time string (HH:MM or HH:MM:SS, 24h format) or empty
 *   onChange     - called with synthetic event { target: { name, value } }
 *   name        - input name
 *   disabled    - boolean
 *   style       - container style override
 *   inputStyle  - input element style override
 *   step        - minute interval (default 15)
 *   placeholder - placeholder text
 */
export default function TimePickerInput({
  value, onChange, name, disabled, style, inputStyle,
  step = 15, placeholder, required, className, id, ...rest
}) {
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Generate time slots
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll to selected value when opened
  useEffect(() => {
    if (open && listRef.current && value) {
      const normalized = value.substring(0, 5);
      const idx = slots.indexOf(normalized);
      if (idx >= 0 && listRef.current) {
        const itemH = 36;
        listRef.current.scrollTop = Math.max(0, idx * itemH - 72);
      }
    }
  }, [open]); // eslint-disable-line

  // Position dropdown
  const [dropUp, setDropUp] = useState(false);
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropUp(rect.bottom + 260 > window.innerHeight);
    }
  }, [open]);

  const fireChange = useCallback((val) => {
    if (onChange) {
      onChange({ target: { name: name || '', value: val } });
    }
  }, [onChange, name]);

  const handleSelect = (slot) => {
    fireChange(slot);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    fireChange('');
    setOpen(false);
  };

  // Format display value
  const formatTime = (t) => {
    if (!t) return '';
    const [hStr, mStr] = t.split(':');
    const h = parseInt(hStr);
    const m = mStr || '00';
    const period = h >= 12 ? (isZh ? '下午' : 'PM') : (isZh ? '上午' : 'AM');
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${period}`;
  };

  const displayValue = value ? formatTime(value) : '';
  const normalizedValue = value ? value.substring(0, 5) : '';

  const S = {
    container: { position: 'relative', display: 'inline-block', ...style },
    input: {
      padding: '8px 32px 8px 10px', fontSize: 13, border: '1px solid #D6D0C7', borderRadius: 2,
      background: disabled ? '#EFEAE2' : '#FFFDF9', cursor: disabled ? 'default' : 'pointer',
      outline: 'none', fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif", color: '#111111',
      width: '100%', boxSizing: 'border-box',
      ...inputStyle
    },
    icon: {
      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
      pointerEvents: 'none', color: '#6E685F', fontSize: 14
    },
    dropdown: {
      position: 'absolute', [dropUp ? 'bottom' : 'top']: '100%',
      left: 0, marginTop: dropUp ? 0 : 4, marginBottom: dropUp ? 4 : 0,
      zIndex: 9999, background: '#FFFDF9', borderRadius: 2, boxShadow: 'none',
      border: '1px solid #E4DFD7', width: 180, maxHeight: 250, overflowY: 'auto',
      fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif", animation: 'fadeIn 0.15s ease'
    },
    slot: (selected) => ({
      padding: '8px 14px', fontSize: 13, cursor: 'pointer',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: selected ? '#111111' : 'transparent',
      color: selected ? '#FFFDF9' : '#3A3835',
      fontWeight: selected ? 600 : 400,
      transition: 'background 0.1s'
    }),
    period: (selected) => ({
      fontSize: 10, fontWeight: 600,
      color: selected ? 'rgba(255,255,255,0.8)' : '#6E685F',
      marginLeft: 8
    }),
    clearBtn: {
      position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
      cursor: 'pointer', color: '#6E685F', fontSize: 12, border: 'none', background: 'none',
      padding: 2, display: 'flex', alignItems: 'center'
    }
  };

  return (
    <div ref={containerRef} style={S.container} className={className}>
      <input
        readOnly
        id={id}
        value={displayValue}
        placeholder={placeholder || (isZh ? '选择时间' : 'Select time')}
        onClick={() => !disabled && setOpen(!open)}
        style={S.input}
        disabled={disabled}
        required={required}
        autoComplete="off"
        {...rest}
      />
      <span style={S.icon}></span>
      {value && !disabled && (
        <button type="button" onClick={handleClear} style={S.clearBtn} title={isZh ? '清除' : 'Clear'}>
          &times;
        </button>
      )}

      {open && (
        <div ref={listRef} style={S.dropdown} onClick={e => e.stopPropagation()}>
          {slots.map(slot => {
            const sel = slot === normalizedValue;
            const [hStr] = slot.split(':');
            const h = parseInt(hStr);
            const period = h >= 12 ? (isZh ? '下午' : 'PM') : (isZh ? '上午' : 'AM');
            return (
              <div key={slot} style={S.slot(sel)}
                onClick={() => handleSelect(slot)}
                onMouseEnter={e => { if (!sel) e.target.style.background = '#EFEAE2'; }}
                onMouseLeave={e => { if (!sel) e.target.style.background = 'transparent'; }}>
                <span>{formatTime(slot)}</span>
                <span style={S.period(sel)}>{period}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
